import { generateClient } from 'aws-amplify/api';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';

const client = generateClient();

/**
 * Create a new status history record
 * @param {Object} params - The parameters for creating a status history record
 * @param {string} params.submissionId - The ID of the submission
 * @param {string} params.previousStatus - The previous status (can be null for initial status)
 * @param {string} params.newStatus - The new status
 * @param {string} params.changedBy - Who made the change
 * @param {string} params.reason - Optional reason for the change
 * @returns {Promise<Object>} The created status history record
 */
export const createStatusHistoryRecord = async ({ submissionId, previousStatus, newStatus, changedBy, reason = null }) => {
  try {
    const result = await client.graphql({
      query: mutations.createSubmissionStatusHistory,
      variables: {
        input: {
          submissionId,
          previousStatus,
          newStatus,
          changedAt: new Date().toISOString(),
          changedBy,
          reason
        }
      }
    });
    return result.data.createSubmissionStatusHistory;
  } catch (error) {
    console.error('[ERROR] Error creating status history record:', error);
    throw error;
  }
};

/**
 * Get status history for a submission, sorted by changedAt (most recent first)
 * @param {string} submissionId - The ID of the submission
 * @param {number} limit - Maximum number of records to return
 * @returns {Promise<Array>} Array of status history records
 */
export const getStatusHistoryBySubmissionId = async (submissionId, limit = 100) => {
  try {
    let allItems = [];
    let nextToken = null;

    do {
      const result = await client.graphql({
        query: queries.submissionStatusHistoriesBySubmissionIdAndChangedAt,
        variables: {
          submissionId,
          sortDirection: 'DESC',
          limit,
          nextToken
        }
      });

      const data = result.data.submissionStatusHistoriesBySubmissionIdAndChangedAt;
      allItems = [...allItems, ...data.items];
      nextToken = data.nextToken;
    } while (nextToken && allItems.length < limit);

    return allItems;
  } catch (error) {
    console.error('[ERROR] Error fetching status history:', error);
    throw error;
  }
};

/**
 * Get the most recent status change for a submission
 * @param {string} submissionId - The ID of the submission
 * @returns {Promise<Object|null>} The most recent status history record or null
 */
export const getLatestStatusChange = async (submissionId) => {
  try {
    const result = await client.graphql({
      query: queries.submissionStatusHistoriesBySubmissionIdAndChangedAt,
      variables: {
        submissionId,
        sortDirection: 'DESC',
        limit: 1
      }
    });

    const items = result.data.submissionStatusHistoriesBySubmissionIdAndChangedAt.items;
    return items.length > 0 ? items[0] : null;
  } catch (error) {
    console.error('[ERROR] Error fetching latest status change:', error);
    throw error;
  }
};

/**
 * Get all status history records (for admin/reporting purposes)
 * @param {number} limit - Maximum number of records to return
 * @returns {Promise<Array>} Array of all status history records
 */
export const getAllStatusHistory = async (limit = 1000) => {
  try {
    let allItems = [];
    let nextToken = null;

    do {
      const result = await client.graphql({
        query: queries.listSubmissionStatusHistories,
        variables: {
          limit: Math.min(limit - allItems.length, 1000),
          nextToken
        }
      });

      const data = result.data.listSubmissionStatusHistories;
      allItems = [...allItems, ...data.items];
      nextToken = data.nextToken;
    } while (nextToken && allItems.length < limit);

    return allItems;
  } catch (error) {
    console.error('[ERROR] Error fetching all status history:', error);
    throw error;
  }
};
